/* eslint-disable max-lines */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { Keys, locale } from 'src/i18n';
import {
  getCurrentEntityId,
  getEntityListParams,
} from 'src/models/details/selectors';
import {
  setCurrentEntityId,
  setDetails,
  setDetailsActivitiesData,
} from 'src/models/details/slice';
import { getFiltersByContext } from 'src/models/filters/selectors';
import { getRevyreWorkflowConfig } from 'src/models/lmsConfig/queries';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { removeFirstPendingActivity } from 'src/models/lmsConfig/slice';
import {
  REVYRE_FORM,
  REVYRE_WORKFLOW_DETAILS_360,
  SUPERVISOR_REVIEW_DOMAINTYPE,
} from 'src/modules/constants';
import { getState } from 'src/store';
import {
  ANALYTICS_TYPE_REVYRE_FORM_SUBMIT,
  ANALYTICS_TYPE_REVYRE_PHONE_CALL,
} from 'src/workspace/analytics/constants';
// eslint-disable-next-line vymo-ui/restrict-import
import { trackAnalyticsActions } from 'src/workspace/analytics/queries';
import axios from 'src/workspace/axios';
import { removeDialog, renderModule } from 'src/workspace/slice';
import { closePortal } from 'src/workspace/utils';
// eslint-disable-next-line vymo-ui/restrict-import
import { setNotification } from '../details360/slice';
// eslint-disable-next-line vymo-ui/restrict-import
import { fetchEntityDetails, fetchTrails } from '../details360/thunk';
import {
  REVYRE,
  REVYRE_FORM_CREATE_TYPE,
  REVYRE_FORM_DETAILS_CREATE_TYPE,
} from './constants';
import { CASE } from './details/constants';
import { getWorkflowCode } from './form/queries';
import { RevyreFormProps } from './form/types';
import {
  getCurrentWorkflowType,
  getDomainConfigByType,
  getEntityListMetaData,
} from './selectors';
import {
  clearNotification,
  resetEntityListData,
  setAllocationTypes,
  setCurrentBulkDownloadJobId,
  setCurrentWorkflowType,
  setDomainType,
  setEntityListData,
  setError,
  setLoading,
  setModuleConfigData,
  setNotification as setRevyreListNotification,
} from './slice';
import { BulkDownloadJobPayload, DomainType, WorkflowType } from './types';

const isRevyreMock = localStorage.getItem('isRevyreMock') === 'true';
const url = isRevyreMock
  ? 'https://a556d0d3-4027-4273-ae26-c6bfb566a903.mock.pstmn.io'
  : '';

export const BASE_URL = `${url}/vymo/v3/workflow`;

export const getRequestHeaders = () => ({
  'x-vymo-device-type': 'web',
});

export const initiatePhoneCall = (payload) => async () => {
  try {
    await axios.post(`${BASE_URL}/phonecall`, payload, {
      headers: getRequestHeaders(),
    });
    trackAnalyticsActions({
      type: ANALYTICS_TYPE_REVYRE_PHONE_CALL,
      isPageView: false,
      module: payload.workflowType,
      payload,
    });

    return true;
  } catch (err: any) {
    const details = err?.response?.data?.details;
    const description = details?.description;
    const subErrorCode = details?.subErrorCode;

    if (
      REVYRE.COLLECT_IQ.STATUS_ERROR_CODE_WITH_DESCRIPTION.includes(
        subErrorCode,
      )
    ) {
      // eslint-disable-next-line no-console
      console.log(description);
    } else {
      // eslint-disable-next-line no-console
      console.log(err);
    }
    return false;
  }
};

const createWorkflow = (workflowType, payload) =>
  axios.post(`${BASE_URL}/${workflowType}`, payload, {
    headers: getRequestHeaders(),
  });

const createDomain = (domainType, payload) =>
  axios.post(`${BASE_URL}/${domainType}`, payload, {
    headers: getRequestHeaders(),
  });

const updateWorkflow = (workflowType, entityId, eventId, payload) =>
  axios.patch(`${BASE_URL}/${workflowType}/${entityId}/${eventId}`, payload, {
    headers: getRequestHeaders(),
  });

const updateDomain = (domainType, entityId, eventId, payload) =>
  axios.patch(`${BASE_URL}/${domainType}/${entityId}/${eventId}`, payload, {
    headers: getRequestHeaders(),
  });

export const fetchModuleConfig = createAsyncThunk(
  'moduleConfig',
  async (
    {
      domainType,
      workflowType,
      refresh = false,
    }: {
      domainType: DomainType;
      workflowType?: WorkflowType;
      refresh?: boolean;
    },
    { dispatch },
  ) => {
    workflowType = workflowType ?? getCurrentWorkflowType(getState());

    const moduleConfig = getDomainConfigByType(
      getState(),
      domainType,
      workflowType,
    );

    dispatch(setError({ message: '' }));

    if (moduleConfig && !refresh) {
      return;
    }

    try {
      const moduleConfigApi = `${url}/vymo/v3/workflow/${
        domainType === 'workflow' ? workflowType : domainType
      }/config`;
      dispatch(setLoading({ loading: true }));
      const response = await axios.get(moduleConfigApi, {
        headers: getRequestHeaders(),
      });

      const configData = response?.data?.payload?.flows?.[0];
      dispatch(
        setModuleConfigData({
          domainType,
          workflowType,
          data: configData,
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const fetchEntityList = createAsyncThunk(
  'entityList',
  async (
    {
      domainType,
      payload,
      preserveNotification = false,
    }: { domainType: DomainType; payload; preserveNotification?: boolean },
    { dispatch },
  ) => {
    try {
      const workflowType = getCurrentWorkflowType(getState());
      const entityListApi = `${BASE_URL}/${
        domainType !== 'workflow' ? domainType : workflowType
      }/list`;
      dispatch(setLoading({ loading: true }));
      if (!preserveNotification) {
        dispatch(clearNotification());
      }
      const response = await axios.post(entityListApi, payload, {
        headers: getRequestHeaders(),
      });
      dispatch(setEntityListData({ data: response?.data }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const triggerBulkDownloadJob = createAsyncThunk<
  void,
  BulkDownloadJobPayload
>('triggerBulkDownloadJob', async (payload, { dispatch }) => {
  try {
    const downloadListApi = `${BASE_URL}/job/?category=workflow`;

    dispatch(setError({ message: '' }));
    const response = await axios.post(downloadListApi, payload, {
      headers: getRequestHeaders(),
    });
    const { id: jobId } = response.data?.payload ?? {};
    dispatch(setCurrentBulkDownloadJobId(jobId));
    dispatch(
      renderModule({
        id: 'DDP_DETAILS',
        props: { jobId },
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
});

export const fetchBulkAllocationTypes = createAsyncThunk(
  'revyre/fetchBulkAllocationTypes',
  async (module: string, { dispatch }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/bulk-allocation/${module}/getAllocationTypes`,
        {
          headers: getRequestHeaders(),
        },
      );

      const allocationTypes = response?.data?.payload ?? [];
      dispatch(setAllocationTypes({ bulkUpload: allocationTypes }));

      return allocationTypes;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      dispatch(setAllocationTypes({ bulkUpload: [] }));
      return [];
    }
  },
);

export const fetchBulkRevocationTypes = createAsyncThunk(
  'revyre/fetchBulkRevocationTypes',
  async (module: string, { dispatch }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/bulk-revocation/${module}/getAllocationTypes`,
        {
          headers: getRequestHeaders(),
        },
      );

      const allocationTypes = response?.data?.payload ?? [];
      dispatch(setAllocationTypes({ bulkRevoke: allocationTypes }));

      return allocationTypes;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      dispatch(setAllocationTypes({ bulkRevoke: [] }));
      return [];
    }
  },
);

export const fetchRevyreV1EntityDetails = createAsyncThunk(
  'entityDetails',
  async (
    {
      domainType,
      workflowType,
      entityId,
    }: {
      domainType: DomainType;
      workflowType?: WorkflowType;
      entityId: string;
      type?: string;
    },
    { dispatch },
  ) => {
    workflowType = workflowType ?? getCurrentWorkflowType(getState());
    try {
      const entityDetailsApi = `${url}/vymo/v3/workflow/${
        domainType !== 'workflow' ? domainType : workflowType
      }/${entityId}`;
      dispatch(setLoading({ loading: true }));
      const response = await axios.get(entityDetailsApi, {
        headers: getRequestHeaders(),
      });

      dispatch(
        setDetails({
          entityId,
          data: response?.data,
          revyre: true,
        }),
      );

      const associatedWorkflow =
        response?.data?.payload?.mutatedEntity?.workflowType;
      const associatedWorkflowId = getWorkflowCode(
        response?.data?.payload?.mutatedEntity?.workflowId,
      );

      if (associatedWorkflow) {
        dispatch(setCurrentWorkflowType(associatedWorkflow));
        dispatch(
          fetchModuleConfig({
            // @ts-ignore
            domainType: 'workflow',
          }),
        );
      }
      if (associatedWorkflowId) {
        dispatch(
          fetchRevyreV1EntityDetails({
            domainType: 'workflow',
            workflowType: associatedWorkflow,
            entityId: associatedWorkflowId,
          }),
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const fetchAudits = async (payload: {
  serviceType: string;
  serviceId: string;
}) =>
  axios.post(`${BASE_URL}/auditConsumer/audits`, payload, {
    headers: getRequestHeaders(),
  });

export const fetchHistory = async (payload: {
  serviceType: string;
  serviceId: string;
}) =>
  axios.post(`${BASE_URL}/auditConsumer/history`, payload, {
    headers: getRequestHeaders(),
  });

export const fetchEntityActivities = createAsyncThunk(
  'entityHistoryAndAudit',
  async (
    {
      serviceType,
      entityId,
    }: {
      serviceType: WorkflowType | DomainType;
      entityId: string;
    },
    { dispatch },
  ) => {
    try {
      const [auditRes, historyRes] = await Promise.all([
        fetchAudits({
          serviceType,
          serviceId: entityId,
        }),
        fetchHistory({
          serviceType,
          serviceId: entityId,
        }),
      ]);
      const payload = {
        audit: auditRes?.data?.payload ?? [],
        history: historyRes?.data?.payload ?? [],
      };

      dispatch(
        setDetailsActivitiesData({
          entityId,
          data: payload ?? {},
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      dispatch(setLoading({ loading: false }));
    }
  },
);

export const formSubmit = createAsyncThunk(
  'formSubmit',
  // eslint-disable-next-line max-lines-per-function, complexity
  async (
    {
      type,
      domainType,
      workflowType,
      entityId,
      eventId,
      payload,
      isBlockingScreen,
      buttonMetadata,
    }: {
      type: string;
      domainType: DomainType;
      workflowType?: WorkflowType;
      entityId: string;
      eventId: string;
      payload;
      isBlockingScreen: Boolean | undefined;
      buttonMetadata: RevyreFormProps['buttonMetadata'];
    },
    { dispatch },
  ) => {
    try {
      let response: AxiosResponse | null = null;

      dispatch(setLoading({ loading: true }));
      dispatch(setError({ message: '' }));
      workflowType = workflowType ?? getCurrentWorkflowType(getState());

      trackAnalyticsActions({
        type: ANALYTICS_TYPE_REVYRE_FORM_SUBMIT,
        isPageView: false,
        module: domainType,
        source: type,
        payload,
      });
      switch (domainType) {
        case 'activity':
          if (
            type === 'detailsCreate' ||
            type === 'create' ||
            type === 'createGlobal'
          ) {
            try {
              response = await createDomain(domainType, payload);

              if (type === 'createGlobal') {
                closePortal?.();
              }

              if (type === REVYRE_FORM_CREATE_TYPE) {
                dispatch(
                  setRevyreListNotification({
                    message: locale(Keys.ACTIVITY_ADDED_SUCCESSFULLY),
                    id: String(Date.now()),
                    variant: 'success',
                  }),
                );

                dispatch(
                  fetchEntityList({
                    domainType,
                    payload: { pageSize: 10, pageNumber: 1 },
                    preserveNotification: true,
                  }),
                );
              } else if (type === REVYRE_FORM_DETAILS_CREATE_TYPE) {
                dispatch(
                  setNotification({
                    message: locale(Keys.ACTIVITY_ADDED_SUCCESSFULLY),
                    messageId: String(Date.now()),
                    variant: 'success',
                  }),
                );
                dispatch(
                  fetchEntityDetails({
                    entityId,
                    domainType: 'workflow',
                    workflowType,
                    revyre: true,
                  }),
                );

                // eslint-disable-next-line max-depth
                if (workflowType === CASE) {
                  dispatch(
                    fetchTrails({
                      props: { workflowType },
                      payload: {
                        filters: { workflowId: entityId },
                      },
                    }),
                  );
                }
              }
            } catch (err) {
              // eslint-disable-next-line no-console
              console.log(err);
            }
          }

          if (type === 'update') {
            response = await updateDomain(
              domainType,
              entityId,
              eventId,
              payload,
            );

            dispatch(
              setNotification({
                message: locale(Keys.ACTIVITY_UPDATED_SUCCESSFULLY),
                messageId: String(Date.now()),
                variant: 'success',
              }),
            );

            if (isBlockingScreen) {
              dispatch(removeFirstPendingActivity());
            }

            dispatch(
              fetchEntityDetails({ domainType, entityId, revyre: true }),
            );
          }

          break;

        case 'payment':
          if (
            type === 'detailsCreate' ||
            type === 'create' ||
            type === 'createGlobal'
          ) {
            response = await createDomain(domainType, payload);
          }

          if (type === 'update') {
            if (buttonMetadata?.parentAssociateWorkflowType) {
              response = await updateWorkflow(
                workflowType,
                entityId,
                eventId,
                payload,
              );
              dispatch(
                fetchEntityDetails({
                  entityId,
                  domainType: 'workflow',
                  workflowType,
                  revyre: true,
                }),
              );
              dispatch(
                setNotification({
                  message: locale(Keys.PAYMENT_UPDATED_SUCCESSFULLY),
                  messageId: String(Date.now()),
                  variant: 'success',
                }),
              );
            } else {
              response = await updateDomain(
                domainType,
                entityId,
                eventId,
                payload,
              );
              dispatch(fetchRevyreV1EntityDetails({ domainType, entityId }));
            }
          }

          break;

        case SUPERVISOR_REVIEW_DOMAINTYPE:
          if (
            type === 'detailsCreate' ||
            type === 'create' ||
            type === 'createGlobal'
          ) {
            response = await createDomain(domainType, payload);
          }
          break;

        default:
          if (type === 'create') {
            response = await createWorkflow(workflowType, payload);
            dispatch(
              renderModule({
                id: REVYRE_WORKFLOW_DETAILS_360,
                props: {
                  domainType,
                  workflowType,
                  entityId: response?.data?.payload?.id,
                },
              }),
            );
          }
          if (type === 'detailsCreate') {
            response = await createWorkflow(workflowType, payload);

            const currentEntityId = getCurrentEntityId(getState());

            dispatch(
              fetchRevyreV1EntityDetails({
                domainType,
                entityId: currentEntityId,
              }),
            );
          }
          if (type === 'createGlobal') {
            response = await createWorkflow(workflowType, payload);
            closePortal();
          }
          if (type === 'update') {
            response = await updateWorkflow(
              workflowType,
              entityId,
              eventId,
              payload,
            );
            dispatch(fetchRevyreV1EntityDetails({ domainType, entityId }));
          }

          break;
      }

      if (response) {
        dispatch(removeDialog({ id: REVYRE_FORM }));
      }
    } catch (error: any) {
      const errorResponse =
        error?.response?.data?.description ?? error?.message;
      // eslint-disable-next-line no-console
      console.log(errorResponse);
    } finally {
      dispatch(setLoading({ loading: false }));
    }
    // dispatch(removeDialog({ id: 'REVYRE_FORM' }));
  },
);

const onUpdate = async ({ dispatch, props }) => {
  const { workflowType, domainType, mode } = props;
  const currentEntityId = getCurrentEntityId(getState());
  const filterContext = `revyre_${domainType}_${workflowType || 'default'}`;
  const persistedFilters = getFiltersByContext(getState(), filterContext);
  const { pageSize } = getEntityListMetaData(getState());
  const { filters: tableParamsFilters } = getEntityListParams(getState());
  const filters =
    Object.keys(persistedFilters).length > 0
      ? persistedFilters
      : tableParamsFilters || {};
  let { currentPage } = getEntityListMetaData(getState());

  if (mode !== 'details') {
    dispatch(setCurrentEntityId(''));
    currentPage = 1;
    dispatch(resetEntityListData());
  }

  dispatch(setDomainType(domainType));
  if (workflowType) {
    dispatch(setCurrentWorkflowType(workflowType));
  }
  await dispatch(fetchModuleConfig({ domainType }));
  if (!currentEntityId) {
    dispatch(
      fetchEntityList({
        domainType,
        payload: {
          filters,
          page: {
            pageSize,
            pageNumber: currentPage,
          },
        },
      }),
    );
  }
};

export const syncWorkflowConfigToSlice = (dispatch) => {
  dispatch(clearNotification());
  const revyreWorkflow = getRevyreWorkflowConfig();
  // Setting the workflow config to store from what we have in lms
  revyreWorkflow.forEach((workflowData) => {
    const workflowType = workflowData.code;
    // TODO - hardcoded backends should send the correct data
    const domainType = 'workflow';
    const workflowConfig = getRevyreConfig(getState())?.[workflowType] ?? null;

    if (workflowConfig) {
      dispatch(
        setModuleConfigData({
          workflowType: workflowType as WorkflowType,
          domainType,
          data: {
            code: workflowType,
            name: workflowData.name,
            ...workflowConfig,
          },
        }),
      );
    }
  });
};

const onActivate = ({ dispatch }) => {
  dispatch(setCurrentEntityId(''));
  syncWorkflowConfigToSlice(dispatch);
};

const onDeactivate = () => {};

export default { onActivate, onDeactivate, onUpdate };
